/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Checkbox, Icon, Label
} from '@blueprintjs/core';
import ModalDialog from 'shared/modal/dialog';
import './action-menus.scss';
import { BackupFileDTO, BackupJobDTO } from 'data/backupRestore/backup.types';
import moment from 'moment';

type Props = {
  name: string;
  deleteTitle: string,
  restoreTitle: string,
  deleteMessage: string | JSX.Element[],
  restoreMessage: string | JSX.Element[],
  onDelete?: () => void;
  onRestore?: (shouldRecreateDB: boolean) => void;
  viewDetails?: () => void;
  jobDetails?: BackupJobDTO;
  backupFile?: BackupFileDTO;
}

const BackupRestoreActionMenus: FunctionComponent<Props> = ({
  name,
  deleteTitle,
  restoreTitle,
  deleteMessage,
  restoreMessage,
  onDelete,
  onRestore,
  viewDetails,
  jobDetails,
  backupFile,
}: Props) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [isViewDetailsDialogOpen, setIsViewDetailsDialogOpen] = useState(false);
  const [shouldRecreateDB, setShouldRecreateDB] = useState(false);
  const { t } = useTranslation();

  const handleOpenDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setIsDeleteDialogOpen(false);
  };

  const handleOpenRestoreDialog = () => {
    setIsRestoreDialogOpen(true);
  };

  const handleCloseRestoreDialog = () => {
    setIsRestoreDialogOpen(false);
  };

  const handleOpenViewDetailsDialog = () => {
    setIsViewDetailsDialogOpen(true);
  }

  const handleCloseViewDetailsDialog = () => {
    setIsViewDetailsDialogOpen(false);
  }

  const handleRestore = () => {
    if (onRestore) {
      onRestore(shouldRecreateDB);
    }
    setIsRestoreDialogOpen(false);
  }

  const renderRestoreJobDialog = () => {
    if (!backupFile || backupFile.product !== 'xcipio') {
      return null;
    }

    return (
      <div>
        <Checkbox onChange={(e) => setShouldRecreateDB(!shouldRecreateDB)} checked={shouldRecreateDB}>
          Drop and Recreate XCIPIO Database
        </Checkbox>
        {shouldRecreateDB && (
          <div className="form-flex-row-css">
            <Icon intent="warning" icon="warning-sign" />{' '}
            <span className="bp3-text-small bp3-text-muted">
              Checking this box will drop and recreate the XCIPIO database.
              Ensure all data is backed up before proceeding.
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderJobDetails = () => {
    if (!jobDetails) {
      return null;
    }

    return (
      <div className="job-details-dialog">
        <div className="form-flex-col-css field-group">
          <Label style={{ fontWeight: 600 }}>Job Name:</Label>
          <Label>{jobDetails?.name}</Label>
        </div>

        <div className="form-flex-col-css field-group">
          <Label style={{ fontWeight: 600 }}>Job ID:</Label>
          <Label>{jobDetails?.id}</Label>
        </div>

        <div className="form-flex-col-css field-group">
          <Label style={{ fontWeight: 600 }}>Type:</Label>
          <Label>{jobDetails?.type}</Label>
        </div>

        <div className="form-flex-col-css field-group">
          <Label style={{ fontWeight: 600 }}>Start time:</Label>
          <Label>{moment(jobDetails?.start_time || undefined).format('LLL')}</Label>
        </div>

        <div className="form-flex-col-css field-group">
          <Label style={{ fontWeight: 600 }}>Completion time:</Label>
          <Label>{moment(jobDetails?.completion_time || undefined).format('LLL')}</Label>
        </div>

        <div className="form-flex-col-css field-group">
          <Label style={{ fontWeight: 600 }}>Status:</Label>
          <Label>{jobDetails?.status}</Label>
        </div>
        {jobDetails?.cause && jobDetails?.cause?.message && (
          <div className="form-flex-col-css field-group">
            <Label style={{ fontWeight: 600, display: 'flex' }}><Icon style={{ marginRight: 4 }} icon="error" intent="danger" />Cause:</Label>
            <Label>{jobDetails?.cause?.message}</Label>
          </div>
        )}
      </div>
    );
  };

  if (name == null) {
    return null;
  }

  return (
    <div
      data-testid="BackupRestoreActionMenus"
      className="action-menus-container"
    >
      {onDelete && (
        <Button
          className="menu-button"
          data-testid="delete-button"
          text={t('collectionFunction.grid.delete')}
          onClick={handleOpenDeleteDialog}
        />
      )}
      {onRestore && (
        <Button
          className="menu-button"
          data-testid="restore-button"
          text={t('backupRestore.grid.restore')}
          onClick={handleOpenRestoreDialog}
        />
      )}
      {jobDetails && (
        <Button
          className="menu-button"
          data-testid="view-details-button"
          text={t('backupRestore.grid.viewDetails')}
          onClick={handleOpenViewDetailsDialog}
        />
      )}
      <ModalDialog
        width="500px"
        isOpen={isDeleteDialogOpen}
        title={deleteTitle}
        displayMessage={deleteMessage}
        actionText={t('backupRestore.deletePopup.action')}
        onSubmit={handleDelete}
        onClose={handleCloseDeleteDialog}
      />
      <ModalDialog
        width="500px"
        isOpen={isRestoreDialogOpen}
        title={restoreTitle}
        customComponent={() => renderRestoreJobDialog()}
        displayMessage={restoreMessage}
        actionText={t('backupRestore.restorePopup.action')}
        onSubmit={handleRestore}
        onClose={handleCloseRestoreDialog}
      />
      {jobDetails && (
        <ModalDialog
          width="500px"
          isOpen={isViewDetailsDialogOpen}
          title="Job Details"
          customComponent={() => renderJobDetails()}
          onClose={handleCloseViewDetailsDialog}
          doNotDisplaySaveButton
        />
      )}
    </div>
  );
};

export default BackupRestoreActionMenus;
